import React, { useCallback, useEffect, useState } from 'react';
import { fetchTeamMembers, inviteTeamMember, removeTeamMember } from '../services/api';
import { Button, Card, Field, Loading, ErrorText, errorMessage } from '../components/ui';

export default function TeamPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [org, setOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try { setData(await fetchTeamMembers()); } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  // Two independent things happen on invite: this app's own branded email
  // (needs EMAIL_SENDER configured) and the actual Microsoft Entra ID invite
  // that lets them sign in at all (needs Graph permissions). Report both -
  // reporting only the first leaves no visibility into whether the one that
  // actually matters worked, and reporting neither (as this page previously
  // did) makes a completely unsent invitation look like a success.
  async function invite() {
    const addr = email.trim();
    setBusy(true); setError(''); setNotice(''); setInviteLink(''); setCopied(false);
    try {
      const d = await inviteTeamMember({ email: addr, role, org: org.trim(), orgType: role === 'guest' ? 'guest' : 'host', packages: [] });

      let msg;
      const emailResult = d?.invite?.email;
      if (d?.invite?.emailed) {
        msg = emailResult?.pending
          ? `Invitation accepted for delivery to ${addr} — the provider hadn't confirmed it yet.`
          : `Invitation emailed to ${addr}.`;
      } else {
        // Three different things end up here and they have three different
        // fixes. Collapsing them all into "email isn't configured" was actively
        // misleading when email IS configured and the provider rejected the
        // send - the real reason came back in the response and was thrown away,
        // leaving no way to tell a missing setting from a refused sender
        // address without reading the server logs.
        let why;
        if (emailResult?.reason === 'no_app_url_configured') {
          why = "the server has no app URL set (CONTROL_PLANE_APP_URL), so it couldn't email a link";
        } else if (emailResult?.skipped || emailResult?.reason === 'email_not_configured') {
          why = "email isn't set up on the server";
        } else if (emailResult?.error) {
          const via = emailResult.transport === 'acs' ? 'the email provider' : 'Microsoft Graph';
          const from = emailResult.sender ? ` (sending from ${emailResult.sender})` : '';
          why = `${via} rejected the send${from}: ${emailResult.error}`;
        } else {
          why = 'the email could not be sent';
        }
        msg = `Invite ready for ${addr} — ${why}. Copy this link and send it to them:`;
        // Prefer the server's link, but fall back to this app's own origin when
        // no app URL is configured server-side. The browser always knows where
        // it is; the server only knows if someone told it.
        setInviteLink(d?.invite?.url || (d?.invite?.token
          ? `${window.location.origin}/accept-invite?token=${encodeURIComponent(d.invite.token)}`
          : ''));
      }

      // Not configured is not the same as failed. inviteEntraGuest returns
      // ok:false WITH skipped:true when the AAD credentials are absent, which
      // read as "failed: unknown error" - the least useful thing it could say,
      // since the fix is to set three app settings rather than to debug Graph.
      const entra = d?.entraInvite;
      if (entra?.skipped) {
        msg += entra.reason === 'entra_invite_not_configured'
          ? " Microsoft sign-in: not set up on the server (AAD_TENANT_ID / AAD_CLIENT_ID / AAD_CLIENT_SECRET), so an external guest can't sign in yet."
          : ' Microsoft sign-in: skipped.';
      } else if (entra?.ok) {
        msg += entra.alreadyExists
          ? ' Microsoft sign-in: this person can already sign in with Microsoft.'
          : " Microsoft sign-in: invite sent — they'll get an email from Microsoft to accept.";
      } else if (entra && entra.ok === false) {
        msg += ` Microsoft sign-in invite failed: ${entra.error || 'unknown error'}. They won't be able to sign in until this is resolved.`;
      }
      setNotice(msg);

      setEmail(''); setOrg('');
      load();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
    } catch {
      // Clipboard is blocked outside a secure context - the input is already
      // selectable, so the link is still gettable by hand.
      setCopied(false);
    }
  }

  const members = data?.members || [];
  // Straight from the API's own canManageSeats check, so the UI cannot get out
  // of step with what the server will accept. Absent until the first load
  // resolves, which correctly reads as "not yet allowed".
  const canManage = Boolean(data?.canManage);

  return (
    <>
      <h1>Team</h1>
      <p className="sub">People with a seat in this organisation. Guests only see the packages assigned to their own organisation.</p>
      <ErrorText>{error}</ErrorText>

      {/* canManage comes from the API's own canManageSeats check, so the UI
          agrees with what the server will actually allow. The owner is never
          removable - the backend returns cannot_remove_owner - so offering it
          is only ever a dead end. */}
      {loading ? <Loading /> : members.map((m) => (
        <Card key={m.email} title={m.name || m.email} subtitle={[m.role, m.org].filter(Boolean).join(' · ')}>
          {canManage && m.role !== 'owner' && (
            <Button compact danger onClick={async () => { await removeTeamMember(m.email); load(); }}>Remove</Button>
          )}
        </Card>
      ))}
      {!loading && !members.length && <p className="muted">No members listed. This usually means multi-tenant mode is off, or this account isn't an admin.</p>}

      {canManage && (
      <Card title="Invite someone">
        <Field label="Email"><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Role">
          <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="guest">Guest (external)</option>
          </select>
        </Field>
        <Field label="Organisation" hint="For guests, this is the supplier organisation whose packages they may see."><input className="input" value={org} onChange={(e) => setOrg(e.target.value)} /></Field>
        <Button onClick={invite} disabled={!email.trim() || busy}>{busy ? 'Inviting…' : 'Send invite'}</Button>

        {notice ? <p className="sub" style={{ marginTop: 12 }}>{notice}</p> : null}
        {inviteLink ? (
          <div className="row" style={{ marginTop: 8, alignItems: 'center' }}>
            <input className="input" readOnly value={inviteLink} onFocus={(e) => e.target.select()} style={{ flex: 1 }} />
            <Button compact secondary onClick={copyLink}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        ) : null}
      </Card>
      )}
    </>
  );
}
